import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useShallowEqualSelector } from '@/hooks';
import {
	useIsCollaborator,
	useIsExtendedCollaborator,
	usePaymentMethod,
	useSubscriptionInfo
} from '@/hooks/user';
import Add from '@/icons/add.svg';
import Delete from '@/icons/delete.svg';
import Refresh from '@/icons/refresh.svg';
import { PAYMENT_STATUS } from '@/models/payment';
import { SUBSCRIPTION_STATUS } from '@/models/subscription';
import { setPlansModalOpen } from '@/store/checkout';
import { paymentMethodLogos, walletLogos } from '@/utils/payment';

import Card from '../Card';
import Description from './Description';
import History from './History';
import PlanStatusChip from './PlanStatusChip';
import * as Modals from './modals/modals';

function Subscription() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const isCollaborator = useIsCollaborator();
	const isExtendedCollaborator = useIsExtendedCollaborator();

	const { hasPaymentMethod, info } = usePaymentMethod();

	const walletLogo = walletLogos[info.walletType];

	const { lastPayment } = useShallowEqualSelector(state => ({
		lastPayment: state.billing.rows[0]
	}));

	const [cancelSubscriptionModalOpen, setCancelSubscriptionModalOpen] =
		useState(false);
	const [removePaymentMethodModalOpen, setRemovePaymentMethodModalOpen] =
		useState(false);

	const [updatePaymentMethodModalOpen, setUpdatePaymentMethodModalOpen] =
		useState(false);

	const changePlanModalOpen = useSelector(
		state => state.checkout.plansModalOpen
	);

	const subscriptionInfo = useSubscriptionInfo();

	const {
		cancellationRequested,
		status: subscriptionStatus,
		isTrial,
		parsedPlan,
		isExpired,
		isPending,
		isCanceled
	} = subscriptionInfo;

	let paymentMethodIcon = null;
	let paymentMethodLabel = '';
	let paymentMethodAlt = '';

	if (info?.type === 'card') {
		paymentMethodIcon =
			paymentMethodLogos[info.cardBrand] ||
			paymentMethodLogos[info.cardType] ||
			paymentMethodLogos[info.type];
		paymentMethodLabel = `**** **** **** ${info.cardLast4}`;
		paymentMethodAlt = info?.cardBrand;
	} else if (info?.type === 'link') {
		paymentMethodIcon = paymentMethodLogos.link;
		paymentMethodLabel = info.linkEmail;
		paymentMethodAlt = info.linkEmail;
	}

	return (
		<>
			<Modals.CancelSubscriptions
				open={cancelSubscriptionModalOpen}
				onOpenChange={setCancelSubscriptionModalOpen}
			/>

			<Modals.ChangePlan
				open={changePlanModalOpen}
				onOpenChange={value => dispatch(setPlansModalOpen(value))}
				confirmButtonText={
					cancellationRequested
						? 'common.reactivate'
						: 'billing.modals.changePlan.title'
				}
			/>

			<Modals.RemovePaymentMethod
				open={removePaymentMethodModalOpen}
				onOpenChange={setRemovePaymentMethodModalOpen}
			/>

			<Modals.UpdatePaymentMethod
				open={updatePaymentMethodModalOpen}
				onOpenChange={setUpdatePaymentMethodModalOpen}
				update={hasPaymentMethod}
			/>

			<div className="flex flex-col gap-[21px] md:flex-row">
				<Card
					buttons={
						isCollaborator || isExtendedCollaborator
							? undefined
							: [
									{
										action: () =>
											setCancelSubscriptionModalOpen(true),
										variant: 'outline',
										size: 'sm',
										text: (
											<span className="flex items-center gap-1 text-xs">
												{t('billing.subscription.cancel')}
											</span>
										),
										visible:
											!cancellationRequested &&
											[
												SUBSCRIPTION_STATUS.TRIALING,
												SUBSCRIPTION_STATUS.ACTIVE
											].includes(subscriptionStatus)
									},
									{
										action: () => {
											if (!hasPaymentMethod) {
												return setUpdatePaymentMethodModalOpen(
													true
												);
											}

											dispatch(setPlansModalOpen(true));
										},
										variant: cancellationRequested
											? 'default'
											: 'outline',
										size: 'sm',
										visible:
											isTrial ||
											cancellationRequested ||
											isExpired ||
											isCanceled ||
											(isPending &&
												lastPayment?.status ===
													PAYMENT_STATUS.ERROR),

										text: (
											<span className="flex items-center gap-1 text-xs">
												{cancellationRequested || isExpired
													? t(
															'billing.modals.reactivatePlan.title'
														)
													: t('billing.modals.changePlan.title')}
											</span>
										)
									}
								]
					}
				>
					<div className="flex justify-between">
						<h2 className="font-semibold leading-7">
							{t(`plans.${parsedPlan}.name`)}
						</h2>

						<PlanStatusChip {...subscriptionInfo} />
					</div>

					<Description subscriptionInfo={subscriptionInfo} />
				</Card>

				<Card
					buttons={
						isCollaborator || isExtendedCollaborator
							? undefined
							: [
									{
										action: () =>
											setUpdatePaymentMethodModalOpen(true),
										variant: 'outline',
										size: 'sm',
										text: (
											<span className="flex items-center gap-1 text-xs">
												{hasPaymentMethod ? (
													<>
														<Refresh className="h-4 w-4" />{' '}
														{t('common.update')}
													</>
												) : (
													<>
														<Add className="h-4 w-4" />{' '}
														{t('common.add')}
													</>
												)}
											</span>
										)
									},
									{
										action: () =>
											setRemovePaymentMethodModalOpen(true),
										variant: 'outline',
										size: 'sm',
										text: (
											<span className="flex items-center gap-1 text-xs">
												<Delete className="h-4 w-4" />{' '}
												{t('common.delete')}
											</span>
										),
										visible: hasPaymentMethod
									}
								]
					}
				>
					<h2 className="font-semibold leading-7">
						{t(`billing.subscription.paymentMethod`)}
					</h2>

					{hasPaymentMethod && (
						<div className="flex items-center gap-2">
							{walletLogo && (
								<img
									className="h-8 w-12"
									src={walletLogo}
									alt={info.walletType}
								/>
							)}

							<img
								className="h-8 w-12 rounded border border-gray-200 p-1"
								src={paymentMethodIcon}
								alt={paymentMethodAlt}
							/>
							<span className="text-xs leading-5 text-grey-500">
								{paymentMethodLabel}
							</span>
						</div>
					)}
				</Card>
			</div>
			<History />
		</>
	);
}

export default Subscription;
